import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { Sidebar } from './Sidebar'
import { Topbar } from './Topbar'
import { BreadcrumbProvider } from './Breadcrumb'

/**
 * Powłoka aplikacji: sidebar 252px + topbar + przewijalna treść.
 * Poniżej 900px sidebar chowa się za hamburgerem (CONTRACTS.md §7 — reszta desktop-first).
 */
export function AppShell({ children }: { children: ReactNode }) {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const location = useLocation()

  useEffect(() => {
    setDrawerOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!drawerOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDrawerOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [drawerOpen])

  return (
    <BreadcrumbProvider>
      <div className="flex h-screen overflow-hidden bg-bg">
        {/* desktop */}
        <div className="hidden shell:block">
          <Sidebar />
        </div>

        {/* mobile — szuflada */}
        <div
          className={cn(
            'fixed inset-0 z-40 shell:hidden',
            drawerOpen ? 'pointer-events-auto' : 'pointer-events-none',
          )}
          aria-hidden={!drawerOpen}
        >
          <div
            onClick={() => setDrawerOpen(false)}
            className={cn(
              'absolute inset-0 bg-[rgba(10,31,46,0.45)] transition-opacity duration-200',
              drawerOpen ? 'opacity-100' : 'opacity-0',
            )}
          />
          <div
            className={cn(
              'absolute left-0 top-0 h-full shadow-pop transition-transform duration-200',
              drawerOpen ? 'translate-x-0' : '-translate-x-full',
            )}
          >
            <Sidebar onNavigate={() => setDrawerOpen(false)} />
          </div>
        </div>

        <div className="flex min-w-0 flex-1 flex-col">
          <Topbar onToggleSidebar={() => setDrawerOpen((open) => !open)} />
          <main className="am-scroll flex-1 overflow-y-auto">
            <div className="mx-auto w-full max-w-[1560px] px-shell py-7">{children}</div>
          </main>
        </div>
      </div>
    </BreadcrumbProvider>
  )
}
